import React, { useState, useEffect } from 'react';
import api from '@/config/axios';
import {
    Clock,
    CheckCircle,
    Circle,
    ArrowRight,
    Loader
} from 'lucide-react';

const InvestorTimeline = () => {
    const [timeline, setTimeline] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchTimeline = async () => {
            try {
                // Fetch timeline for today
                const response = await api.get('/api/timeline');
                if (response.data.success) {
                    setTimeline(response.data.timeline);
                }
            } catch (err) {
                // 404 is expected if no timeline exists for today
                if (err.response && err.response.status === 404) {
                    setTimeline(null);
                } else {
                    console.error('Error fetching timeline:', err);
                    setError('Failed to load timeline');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchTimeline();
    }, []);

    if (loading) {
        return (
            <div className="flex justify-center p-4">
                <Loader className="animate-spin text-primary-500" size={24} />
            </div>
        );
    }

    if (error) {
        return null; // Hide on error
    }

    if (!timeline) {
        return null;
    }

    const steps = [
        { label: 'Market Open', key: 'startTime', time: timeline.startTime },
        { label: 'USDT Buy', key: 'usdtTime', time: timeline.usdtTime },
        { label: 'AED Convert', key: 'usdtToAedTime', time: timeline.usdtToAedTime },
        { label: 'UAE Transfer', key: 'transferToUaeTime', time: timeline.transferToUaeTime },
        { label: 'Completed', key: 'endTime', time: timeline.endTime },
    ];

    const currentTime = new Date();

    const getStepStatus = (stepTime) => {
        const time = new Date(stepTime);
        if (currentTime >= time) return 'completed';
        return 'pending';
    };

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Clock className="text-primary-500" size={20} />
                    Today's Activity
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date().toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}
                </span>
            </div>

            <div className="relative">
                {/* Connecting Line - Desktop */}
                <div className="hidden md:block absolute top-[15px] left-0 w-full h-0.5 bg-gray-200 dark:bg-gray-700 -z-10" />

                <div className="flex flex-col md:flex-row justify-between gap-6 md:gap-0">
                    {steps.map((step, index) => {
                        const status = getStepStatus(step.time);
                        const isLast = index === steps.length - 1;

                        return (
                            <div key={index} className="flex md:flex-col items-center md:items-center gap-4 md:gap-2 flex-1 relative group">
                                {/* Vertical Line for Mobile */}
                                {!isLast && (
                                    <div className="md:hidden absolute left-[15px] top-[30px] w-0.5 h-[calc(100%+24px)] bg-gray-200 dark:bg-gray-700 -z-10" />
                                )}

                                {/* Icon based on status */}
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 z-10 transition-colors duration-300 ${status === 'completed'
                                    ? 'bg-success-100 border-success-500 text-success-600 dark:bg-success-900/30'
                                    : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-400'
                                    }`}>
                                    {status === 'completed' ? (
                                        <CheckCircle size={16} />
                                    ) : (
                                        <Circle size={16} />
                                    )}
                                </div>

                                {/* Content */}
                                <div className="flex-1 md:text-center">
                                    <p className={`text-sm font-medium ${status === 'completed' ? 'text-gray-900 dark:text-gray-100' : 'text-gray-500 dark:text-gray-400'
                                        }`}>
                                        {step.label}
                                    </p>
                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                        {new Date(step.time).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

export default InvestorTimeline;
